import React, { useState } from 'react';
import { RoundingList } from './RoundingList';
import { RoundingForm } from './RoundingForm';
import { BookIcon, ChevronRightIcon, CircleDotIcon, PlusIcon, EditIcon, TrashIcon, CheckCircleIcon, AlertTriangleIcon } from 'lucide-react';
import { OrgUnitSelector } from '../../settings/orgunit/OrgUnitSelector';
import type { OrgUnit } from '../sharedFinancialData';
export type RoundingRule = {
  id: string;
  name: string;
  description: string;
  roundingType: 'Nearest' | 'Up' | 'Down' | 'Swedish' | 'Truncate';
  interval: number;
  scope: 'Global' | 'OrgUnit';
  orgUnitId?: string;
  orgUnitName?: string;
  orgUnitPath?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};
export function RoundingConfiguration({
  locale = 'en-US'
}) {
  const [view, setView] = useState<'list' | 'form'>('list');
  const [editingRule, setEditingRule] = useState<RoundingRule | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [roundingRules, setRoundingRules] = useState<RoundingRule[]>([{
    id: 'round-001',
    name: 'Standard Cents',
    description: 'Round to nearest cent (0.01)',
    roundingType: 'Nearest',
    interval: 0.01,
    scope: 'Global',
    active: true,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'round-002',
    name: 'Nickel Rounding',
    description: 'Round up to nearest 0.05',
    roundingType: 'Up',
    interval: 0.05,
    scope: 'OrgUnit',
    orgUnitId: 'ou-apac-sg',
    orgUnitName: 'Singapore Office',
    orgUnitPath: 'Global Technologies Inc. > APAC Region > Singapore Office',
    active: true,
    createdAt: '2023-01-15T00:00:00Z',
    updatedAt: '2023-01-15T00:00:00Z'
  }, {
    id: 'round-003',
    name: 'Whole Dollar',
    description: 'Round down to nearest dollar',
    roundingType: 'Down',
    interval: 1.0,
    scope: 'OrgUnit',
    orgUnitId: 'ou-latam-mx',
    orgUnitName: 'Mexico Office',
    orgUnitPath: 'Global Technologies Inc. > LATAM Region > Mexico Office',
    active: true,
    createdAt: '2023-02-01T00:00:00Z',
    updatedAt: '2023-02-01T00:00:00Z'
  }, {
    id: 'round-004',
    name: 'Swedish Cash Rounding',
    description: 'Swedish rounding for cash transactions',
    roundingType: 'Swedish',
    interval: 0.5,
    scope: 'Global',
    active: false,
    createdAt: '2023-02-15T00:00:00Z',
    updatedAt: '2023-03-01T00:00:00Z'
  }]);
  const handleAddRule = () => {
    setEditingRule(null);
    setView('form');
  };
  const handleEditRule = (rule: RoundingRule) => {
    setEditingRule(rule);
    setView('form');
  };
  const handleSaveRule = (rule: Omit<RoundingRule, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (editingRule) {
      // Update existing rule
      setRoundingRules(roundingRules.map(r => r.id === editingRule.id ? {
        ...editingRule,
        ...rule,
        updatedAt: new Date().toISOString()
      } : r));
    } else {
      // Create new rule
      const newRule: RoundingRule = {
        id: `round-${Date.now()}`,
        ...rule,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setRoundingRules([...roundingRules, newRule]);
    }
    setView('list');
    setEditingRule(null);
  };
  const handleToggleActive = (id: string) => {
    setRoundingRules(roundingRules.map(rule => rule.id === id ? {
      ...rule,
      active: !rule.active,
      updatedAt: new Date().toISOString()
    } : rule));
  };
  const handleDeleteRule = (id: string) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    if (deletingId) {
      setRoundingRules(roundingRules.filter(rule => rule.id !== deletingId));
      setShowDeleteConfirm(false);
      setDeletingId(null);
    }
  };
  const handleCancelForm = () => {
    setView('list');
    setEditingRule(null);
  };
  const globalRules = roundingRules.filter(r => r.scope === 'Global');
  const orgUnitRules = roundingRules.filter(r => r.scope === 'OrgUnit');
  return <div data-id="element-10707">
      <div className="mb-8" data-id="element-10708">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-10709">
          Rounding Configuration
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-10710">
          Define rounding rules at global and organisation unit levels for use
          across modules
        </p>
      </div>

      {view === 'list' ? <>
          {/* Global Rules */}
          <div className="bg-white shadow rounded-lg mb-6" data-id="element-10711">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-10712">
              <div className="flex items-center justify-between" data-id="element-10713">
                <div data-id="element-10714">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-10715">
                    Global Rules
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-10716">
                    Global rounding rules available to all organisation units
                  </p>
                </div>
                <button onClick={handleAddRule} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10717">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-10718" />
                  Add Rule
                </button>
              </div>
            </div>

            {globalRules.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-10719">
                <p className="text-sm text-gray-500" data-id="element-10720">
                  No global rounding rules configured.
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-10721">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-10722">
                  <thead className="bg-gray-50" data-id="element-10723">
                    <tr data-id="element-10724">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10725">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10726">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10727">
                        Interval
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10728">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10729">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-10730">
                    {globalRules.map(rule => <tr key={rule.id} data-id="element-10731">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10732">
                          <div className="text-sm font-medium text-gray-900" data-id="element-10733">
                            {rule.name}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-10734">
                            {rule.description}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10735">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-10736">
                            {rule.roundingType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-10737">
                          {rule.interval}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10738">
                          {rule.active ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-10739">
                              <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-10740" />
                              Active
                            </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-10741">
                              Inactive
                            </span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-10742">
                          <button onClick={() => handleToggleActive(rule.id)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-10743">
                            {rule.active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => handleEditRule(rule)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-10744">
                            <EditIcon className="h-4 w-4 inline" data-id="element-10745" />
                          </button>
                          <button onClick={() => handleDeleteRule(rule.id)} className="text-red-600 hover:text-red-900" data-id="element-10746">
                            <TrashIcon className="h-4 w-4 inline" data-id="element-10747" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>

          {/* OrgUnit Rules */}
          <div className="bg-white shadow rounded-lg" data-id="element-10748">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-10749">
              <div className="flex items-center justify-between" data-id="element-10750">
                <div data-id="element-10751">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-10752">
                    Organisation Unit Rules
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-10753">
                    Specific rounding rules for individual organisation units
                  </p>
                </div>
              </div>
            </div>

            {orgUnitRules.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-10754">
                <p className="text-sm text-gray-500" data-id="element-10755">
                  No organisation unit-specific rounding rules configured.
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-10756">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-10757">
                  <thead className="bg-gray-50" data-id="element-10758">
                    <tr data-id="element-10759">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10760">
                        Organisation Unit
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10761">
                        Rule Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10762">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10763">
                        Interval
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10764">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10765">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-10766">
                    {orgUnitRules.map(rule => <tr key={rule.id} data-id="element-10767">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10768">
                          <div className="text-sm font-medium text-gray-900" data-id="element-10769">
                            {rule.orgUnitName}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-10770">
                            {rule.orgUnitPath}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10771">
                          <div className="text-sm font-medium text-gray-900" data-id="element-10772">
                            {rule.name}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-10773">
                            {rule.description}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10774">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-10775">
                            {rule.roundingType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-10776">
                          {rule.interval}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10777">
                          {rule.active ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-10778">
                              <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-10779" />
                              Active
                            </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-10780">
                              Inactive
                            </span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-10781">
                          <button onClick={() => handleToggleActive(rule.id)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-10782">
                            {rule.active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => handleEditRule(rule)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-10783">
                            <EditIcon className="h-4 w-4 inline" data-id="element-10784" />
                          </button>
                          <button onClick={() => handleDeleteRule(rule.id)} className="text-red-600 hover:text-red-900" data-id="element-10785">
                            <TrashIcon className="h-4 w-4 inline" data-id="element-10786" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>
        </> : <RoundingForm rule={editingRule} onSave={handleSaveRule} onCancel={handleCancelForm} data-id="element-10787" />}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-10788">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-10789">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-10790">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-10791"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-10792">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-10793">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-10794">
                <div className="sm:flex sm:items-start" data-id="element-10795">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-10796">
                    <AlertTriangleIcon className="h-6 w-6 text-red-600" data-id="element-10797" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-10798">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-10799">
                      Delete Rounding Rule
                    </h3>
                    <div className="mt-2" data-id="element-10800">
                      <p className="text-sm text-gray-500" data-id="element-10801">
                        Are you sure you want to delete this rounding rule? This
                        action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-10802">
                <button type="button" onClick={confirmDelete} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-10803">
                  Delete
                </button>
                <button type="button" onClick={() => setShowDeleteConfirm(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-10804">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}